const FOCUSABLE_SELECTOR =
  "summary, a[href], button:enabled, [tabindex]:not([tabindex^='-']), [draggable], area, input:not([type=hidden]):enabled, select:enabled, textarea:enabled, object, iframe"

/**
 * Gets all focusable elements within a container, including shadow DOM elements.
 * Shadow DOM elements (like close buttons) are placed first in focus order.
 * @param {HTMLElement} container - The container to search within.
 * @returns {HTMLElement[]} Array of visible focusable elements.
 */
export function getFocusableElements(container) {
  const elements = []

  // Check shadow root first if it exists (close button should be first in focus order)
  if (container.shadowRoot) {
    elements.push(
      ...Array.from(container.shadowRoot.querySelectorAll(FOCUSABLE_SELECTOR))
    )
  }

  // Get focusable elements from light DOM (slotted content)
  elements.push(...Array.from(container.querySelectorAll(FOCUSABLE_SELECTOR)))

  // Filter out elements that are not visible or are in hidden containers.
  // Copied from jQuery https://github.com/jquery/jquery/blob/2d4f53416e5f74fa98e0c1d66b6f3c285a12f0ce/src/css/hiddenVisibleSelectors.js
  return elements.filter((element) => {
    // Exclude elements inside aria-hidden, inert, or hidden containers
    if (
      element.closest('[aria-hidden="true"]') ||
      element.closest('[inert]') ||
      element.closest('[hidden]')
    ) {
      return false
    }

    // Exclude elements inside closed <details> (except the <summary> itself)
    const closedDetails = element.closest('details:not([open])')
    if (closedDetails && element.tagName !== 'SUMMARY') {
      // Check if this element is a child of the closed details content (not its summary)
      const detailsSummary = closedDetails.querySelector(':scope > summary')
      if (!detailsSummary || !detailsSummary.contains(element)) {
        return false
      }
    }

    // Check if element is visible
    return !!(
      element.offsetWidth ||
      element.offsetHeight ||
      element.getClientRects().length
    )
  })
}

const trapFocusHandlers = {}

export function trapFocus(container, elementToFocus = container) {
  removeTrapFocus()

  // Store container reference for keydown handler
  trapFocusHandlers.container = container

  trapFocusHandlers.keydown = function (event) {
    if (event.code.toUpperCase() !== 'TAB') return // If not TAB key

    const { container: trapContainer } = trapFocusHandlers

    // Re-get focusable elements in case DOM changed
    const currentElements = getFocusableElements(trapContainer)
    const currentFirst = currentElements[0]
    const currentLast = currentElements[currentElements.length - 1]

    // Check if focus is in container (including shadow DOM)
    const isInContainer =
      trapContainer.contains(document.activeElement) ||
      (trapContainer.shadowRoot &&
        trapContainer.shadowRoot.contains(document.activeElement))

    // If focus is not in container (including shadow DOM), move it to first element
    if (!isInContainer) {
      event.preventDefault()
      currentFirst?.focus()
      return
    }

    // On the last focusable element and tab forward, focus the first element.
    if (event.target === currentLast && !event.shiftKey) {
      event.preventDefault()
      currentFirst?.focus()
      return
    }

    // On the first focusable element and tab backward, focus the last element.
    if (
      (event.target === trapContainer || event.target === currentFirst) &&
      event.shiftKey
    ) {
      event.preventDefault()
      currentLast?.focus()
    }
  }

  // Always attach keydown handler immediately (not on focusin)
  document.addEventListener('keydown', trapFocusHandlers.keydown)

  elementToFocus?.focus()
}

export function removeTrapFocus(elementToFocus = null) {
  document.removeEventListener('keydown', trapFocusHandlers.keydown)
  trapFocusHandlers.container = null

  // Clean up stored references
  trapFocusHandlers.container = null
  trapFocusHandlers.first = null
  trapFocusHandlers.last = null

  if (elementToFocus) elementToFocus.focus()
}

/**
 * Sets aria-hidden on main page content when a dialog is open.
 * @param {boolean} hidden - Whether to hide the background content.
 * @param {HTMLElement} excludeElement - Element to exclude from hiding (the dialog itself).
 * @param {Object} options - Configuration options.
 * @param {boolean} options.includeHeader - Whether to include the header in hiding (default: true).
 */
export function setBackgroundAriaHidden(
  hidden,
  excludeElement = null,
  { includeHeader = true } = {}
) {
  const elements = [
    document.querySelector('main'),
    document.querySelector('footer'),
  ]

  if (includeHeader) {
    elements.unshift(document.querySelector('header'))
  }

  elements.forEach((element) => {
    if (
      element &&
      element !== excludeElement &&
      !excludeElement?.contains(element)
    ) {
      if (hidden) {
        element.setAttribute('aria-hidden', 'true')
      } else {
        element.removeAttribute('aria-hidden')
      }
    }
  })
}

export function onKeyUpEscape(event) {
  if (event.code.toUpperCase() !== 'ESCAPE') return

  const openDetailsElement = event.target.closest('details[open]')
  if (!openDetailsElement) return

  const summaryElement = openDetailsElement.querySelector('summary')
  openDetailsElement.removeAttribute('open')
  summaryElement.setAttribute('aria-expanded', false)
  summaryElement.focus()
}

export function initDisclosureWidgets(summaries) {
  summaries.forEach((summary) => {
    summary.setAttribute('role', 'button')
    summary.setAttribute(
      'aria-expanded',
      summary.parentNode.hasAttribute('open')
    )

    if (summary.nextElementSibling.getAttribute('id')) {
      summary.setAttribute('aria-controls', summary.nextElementSibling.id)
    }

    summary.addEventListener('click', (event) => {
      event.currentTarget.setAttribute(
        'aria-expanded',
        !event.currentTarget.closest('details').hasAttribute('open')
      )
    })

    summary.parentElement.addEventListener('keyup', onKeyUpEscape)
  })
}

// Track elements that have been made inert
const inertElements = new Set()

/**
 * Sets the inert attribute on all siblings of the active element's closest
 * section container, preventing focus from escaping to background content.
 * @param {HTMLElement} activeElement - The element that should remain interactive
 */
export function setInertOnSiblings(activeElement) {
  if (!activeElement) return

  // Find the modal/drawer container - look for common patterns
  const modalContainer =
    activeElement.closest('cart-drawer') ||
    activeElement.closest('[role="dialog"]') ||
    activeElement.closest('details[open]') ||
    activeElement

  // Get all direct children of body
  const bodyChildren = Array.from(document.body.children)

  bodyChildren.forEach((child) => {
    // Skip the modal container itself and script/style elements
    if (
      child === modalContainer ||
      child.contains(modalContainer) ||
      modalContainer.contains(child) ||
      child.tagName === 'SCRIPT' ||
      child.tagName === 'STYLE' ||
      child.tagName === 'LINK' ||
      child.hasAttribute('inert')
    ) {
      return
    }

    // Set inert on sibling elements
    child.setAttribute('inert', '')
    inertElements.add(child)
  })
}

/**
 * Removes the inert attribute from all elements that were previously made inert.
 */
export function removeInertFromSiblings() {
  inertElements.forEach((element) => {
    element.removeAttribute('inert')
  })
  inertElements.clear()
}

/**
 * Filter elements to only those that are visible
 * @param {HTMLElement[]} elements - Array of elements to filter
 * @returns {HTMLElement[]} Array of visible elements
 */
function filterVisible(elements) {
  return elements.filter(
    (element) =>
      !!(
        element.offsetWidth ||
        element.offsetHeight ||
        element.getClientRects().length
      )
  )
}

/**
 * Get menu items (links) from a menu container
 * @param {HTMLElement} container - The menu container element
 * @returns {HTMLElement[]} Array of focusable menu items
 */
export function getMenuItems(container) {
  const items = Array.from(
    container.querySelectorAll('a[href], button:enabled')
  )
  return filterVisible(items)
}

/**
 * Initialize keyboard navigation for a menu container
 * Supports arrow keys, Home/End, and Enter/Space for activation
 * @param {HTMLElement} menuContainer - The menu container to add keyboard navigation to
 * @param {Object} options - Configuration options
 * @param {Function} options.onClose - Callback when menu should close (Escape key)
 * @param {HTMLElement} options.triggerElement - Element to return focus to on close
 */
export function initMenuKeyboardNav(menuContainer, options = {}) {
  const { onClose, triggerElement } = options

  const handleKeyDown = (event) => {
    const menuItems = getMenuItems(menuContainer)
    if (menuItems.length === 0) return

    const currentIndex = menuItems.indexOf(document.activeElement)
    let nextIndex = -1

    switch (event.code.toUpperCase()) {
      case 'ARROWDOWN':
        event.preventDefault()
        // Move to next item, wrap to first if at end
        nextIndex = currentIndex < menuItems.length - 1 ? currentIndex + 1 : 0
        break

      case 'ARROWUP':
        event.preventDefault()
        // Move to previous item, wrap to last if at beginning
        nextIndex = currentIndex > 0 ? currentIndex - 1 : menuItems.length - 1
        break

      case 'HOME':
        event.preventDefault()
        nextIndex = 0
        break

      case 'END':
        event.preventDefault()
        nextIndex = menuItems.length - 1
        break

      case 'ESCAPE':
        event.preventDefault()
        if (onClose) {
          onClose()
        }
        if (triggerElement) {
          triggerElement.focus()
        }
        return

      case 'TAB':
        // Allow natural tab behavior, but track if we're leaving the menu
        if (event.shiftKey && currentIndex === 0) {
          // Tabbing backward from first item - close menu
          if (onClose) {
            onClose()
          }
        } else if (!event.shiftKey && currentIndex === menuItems.length - 1) {
          // Tabbing forward from last item - close menu
          if (onClose) {
            onClose()
          }
        }
        return

      default:
        return
    }

    if (nextIndex >= 0 && nextIndex < menuItems.length) {
      menuItems[nextIndex].focus()
    }
  }

  menuContainer.addEventListener('keydown', handleKeyDown)

  // Return cleanup function
  return () => {
    menuContainer.removeEventListener('keydown', handleKeyDown)
  }
}

/**
 * Initialize keyboard navigation for top-level navigation items
 * Handles left/right arrow keys to move between nav items
 * @param {HTMLElement} navContainer - The navigation container
 * @param {string} itemSelector - CSS selector for nav items
 */
export function initNavKeyboardNav(
  navContainer,
  itemSelector = '.top-level-link'
) {
  const handleKeyDown = (event) => {
    const navItems = Array.from(navContainer.querySelectorAll(itemSelector))
    if (navItems.length === 0) return

    const closestMatch = event.target.closest(itemSelector)
    const currentIndex = navItems.indexOf(closestMatch)
    if (currentIndex === -1) return

    let nextIndex = -1

    switch (event.code.toUpperCase()) {
      case 'ARROWRIGHT':
        event.preventDefault()
        nextIndex = currentIndex < navItems.length - 1 ? currentIndex + 1 : 0
        break

      case 'ARROWLEFT':
        event.preventDefault()
        nextIndex = currentIndex > 0 ? currentIndex - 1 : navItems.length - 1
        break

      default:
        return
    }

    if (nextIndex >= 0 && nextIndex < navItems.length) {
      navItems[nextIndex].focus()
    }
  }

  navContainer.addEventListener('keydown', handleKeyDown)

  // Return cleanup function
  return () => {
    navContainer.removeEventListener('keydown', handleKeyDown)
  }
}
